import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ProgressBarProps {
    estimatedRemainingTime: number;
    isProcessing: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
    estimatedRemainingTime,
    isProcessing
}) => {
    const { t } = useTranslation();
    const [progress, setProgress] = useState(0);
    const [remainingTime, setRemainingTime] = useState(estimatedRemainingTime);

    useEffect(() => {
        setRemainingTime(estimatedRemainingTime);
    }, [estimatedRemainingTime]);

    useEffect(() => {
        if (!isProcessing) return;

        const interval = setInterval(() => {
            setRemainingTime(prev => Math.max(0, prev - 1));

            // Bereken progress gebaseerd op resterende tijd
            if (estimatedRemainingTime > 0) {
                const elapsed = estimatedRemainingTime - remainingTime;
                const progressPercentage = Math.min((elapsed / estimatedRemainingTime) * 100, 95);
                setProgress(progressPercentage);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isProcessing, estimatedRemainingTime, remainingTime]);

    const formatTime = (seconds: number): string => {
        if (seconds <= 0) return '0:00';

        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (!isProcessing) return null;

    return (
        <div className="w-full space-y-2">
            <div className="flex justify-between items-center text-sm">
                <span className="text-gray-700 font-medium">{t('progressBar.processing')}</span>
                <span className="text-gray-500">
                    {remainingTime > 0 ? t('progressBar.remaining', { time: formatTime(remainingTime) }) : t('progressBar.almostDone')}
                </span>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                    className="h-full bg-primary-600 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="text-center text-xs text-gray-500 mt-2">
                {remainingTime > 0
                    ? t('progressBar.processingDescription')
                    : t('progressBar.processingAlmostComplete')
                }
            </div>
        </div>
    );
};

export default ProgressBar; 